import React, { useState, useEffect, useRef } from "react";
import { GripVertical } from "lucide-react";
import { ChatHistory, ChatMessage } from "./ChatHistory";
import { generatePattern } from "../../lib/openai";
import { createPortal } from "react-dom";
import { useAuth } from "../../contexts/AuthContext";
import { ChatPanel } from "./ChatPanel";
import { PreviewPanel } from "./PreviewPanel";
import {
  Checkpoint,
  ProgressTask,
  ToastProps,
  PatternEditorProps,
} from "../../types/pattern";
import { recentPatterns } from "../../data/mockPatterns";
import { Check, X } from "lucide-react";
import { Zap, AlertCircle } from "lucide-react";
import { ProgressOverlay } from "./ProgressOverlay";
import { AIModifier } from "./AIModifier";
import { supabase } from "../../lib/supabase";
import { useNavigate } from "react-router-dom";

export function PatternEditor({
  initialPattern = "",
  title,
  description,
  isTemplate,
}: PatternEditorProps) {
  const { user } = useAuth();
  const [generatedPattern, setGeneratedPattern] = useState(initialPattern);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState<"preview" | "code">("preview");
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>([]);
  const [showCheckpoints, setShowCheckpoints] = useState(false);
  const [showRecentPatterns, setShowRecentPatterns] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  const [progress, setProgress] = useState(0);
  const [copySuccess, setCopySuccess] = useState(false);
  const [toast, setToast] = useState<ToastProps>({
    message: "",
    visible: false,
    type: "success",
  });
  const [progressTasks, setProgressTasks] = useState<ProgressTask[]>([
    { id: "init", name: "Initializing", status: "pending" },
    { id: "create", name: "Creating pattern", status: "pending" },
    { id: "process", name: "Processing components", status: "pending" },
    { id: "finalize", name: "Finalizing pattern", status: "pending" },
  ]);
  const [isChatVisible, setIsChatVisible] = useState(true);
  const [chatWidth, setChatWidth] = useState(450); // Default width in pixels
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Initialize with a starting checkpoint
  useEffect(() => {
    if (initialPattern && checkpoints.length === 0) {
      setCheckpoints([
        {
          id: "initial",
          pattern: initialPattern,
          timestamp: Date.now(),
          description: "Initial pattern",
        },
      ]);
    }
  }, [initialPattern]);

  // Hide toast after timeout
  useEffect(() => {
    if (toast.visible) {
      const timer = setTimeout(() => {
        setToast({ ...toast, visible: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Update progress tasks based on progress percentage
  useEffect(() => {
    if (!isGenerating) {
      // Reset tasks when not generating
      setProgressTasks([
        { id: "init", name: "Initializing", status: "pending" },
        { id: "create", name: "Creating pattern", status: "pending" },
        { id: "process", name: "Processing components", status: "pending" },
        { id: "finalize", name: "Finalizing pattern", status: "pending" },
      ]);
      return;
    }

    // Update task status based on progress
    if (progress <= 10) {
      setProgressTasks((prev) =>
        prev.map((task) =>
          task.id === "init" ? { ...task, status: "in-progress" } : task
        )
      );
    } else if (progress <= 25) {
      setProgressTasks((prev) =>
        prev.map((task) => {
          if (task.id === "init") return { ...task, status: "completed" };
          if (task.id === "create") return { ...task, status: "in-progress" };
          return task;
        })
      );
    } else if (progress <= 75) {
      setProgressTasks((prev) =>
        prev.map((task) => {
          if (task.id === "init" || task.id === "create")
            return { ...task, status: "completed" };
          if (task.id === "process") return { ...task, status: "in-progress" };
          return task;
        })
      );
    } else if (progress < 100) {
      setProgressTasks((prev) =>
        prev.map((task) => {
          if (
            task.id === "init" ||
            task.id === "create" ||
            task.id === "process"
          )
            return { ...task, status: "completed" };
          if (task.id === "finalize") return { ...task, status: "in-progress" };
          return task;
        })
      );
    } else if (progress === 100) {
      setProgressTasks((prev) =>
        prev.map((task) => ({ ...task, status: "completed" }))
      );
    }
  }, [progress, isGenerating]);

  // Handle resize events
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      const dx = e.clientX - startXRef.current;
      const newWidth = Math.max(300, Math.min(800, startWidthRef.current + dx));
      setChatWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.body.style.cursor = "default";
      document.body.style.userSelect = "auto";
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  const startResize = (e: React.MouseEvent) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = chatWidth;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  const handlePromptSubmit = async (prompt: string) => {
    if (!prompt.trim()) return;

    setIsGenerating(true);
    console.log("🔍 PatternEditor: Setting isGenerating to true");

    try {
      // Create a user message with just the prompt
      const userMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "user",
        content: prompt,
        timestamp: Date.now(),
      };
      setChatHistory((prev) => [...prev, userMessage]);

      // Actually generate the pattern using the OpenAI assistant
      const result = await generatePattern(
        prompt,
        generatedPattern || undefined,
        (progress, message) => {
          console.log(
            `🔍 Progress callback: ${progress}%, Message: ${message}`
          );
          setProgress(progress);
          setProgressMessage(message || "Processing your request...");

          // Update the assistant message with progress updates
          const assistantProgressMessage: ChatMessage = {
            id: "progress-" + Date.now(),
            type: "assistant",
            content: message || "Working on your pattern...",
            timestamp: Date.now(),
          };
          setChatHistory((prev) => {
            // Replace the last message if it's from the assistant and is a progress message
            const lastMessage = prev[prev.length - 1];
            if (
              lastMessage &&
              lastMessage.type === "assistant" &&
              lastMessage.id.startsWith("progress-")
            ) {
              return [...prev.slice(0, -1), assistantProgressMessage];
            }
            return [...prev, assistantProgressMessage];
          });
        }
      );

      if (result.error) {
        console.error("🔍 Generation error:", result.error);

        // Add an error message
        const errorMessage: ChatMessage = {
          id: Date.now().toString(),
          type: "assistant",
          content: `Sorry, I encountered an error: ${result.error}`,
          timestamp: Date.now(),
        };
        setChatHistory((prev) => [...prev, errorMessage]);
        return;
      }

      // Update the pattern with the generated result
      setGeneratedPattern(result.pattern);

      // Create an assistant message that's user-friendly (no code)
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "assistant",
        content:
          "I've created the pattern based on your request. You can see it in the preview panel.",
        timestamp: Date.now(),
      };

      // Replace any progress messages with the final message
      setChatHistory((prev) => {
        const filteredMessages = prev.filter(
          (msg) => !(msg.type === "assistant" && msg.id.startsWith("progress-"))
        );
        return [...filteredMessages, assistantMessage];
      });

      // Create a new checkpoint after successful generation
      const newCheckpoint: Checkpoint = {
        id: Date.now().toString(),
        pattern: result.pattern,
        timestamp: Date.now(),
        description: `Changes from: "${prompt.substring(0, 30)}${
          prompt.length > 30 ? "..." : ""
        }"`,
      };
      setCheckpoints((prev) => [...prev, newCheckpoint]);
    } catch (error) {
      console.error("Error during pattern generation:", error);

      // Add an error message
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "assistant",
        content: `Sorry, something went wrong: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        timestamp: Date.now(),
      };
      setChatHistory((prev) => [...prev, errorMessage]);
    } finally {
      console.log("🔍 PatternEditor: Setting isGenerating to false");
      // Add a small delay before setting isGenerating to false
      setTimeout(() => {
        setIsGenerating(false);
        setProgress(0);
        setProgressMessage("");
      }, 1000);
    }
  };

  const rollbackToCheckpoint = (checkpointId: string) => {
    const checkpoint = checkpoints.find((cp) => cp.id === checkpointId);
    if (checkpoint) {
      setGeneratedPattern(checkpoint.pattern);

      // Add a system message about the rollback
      const rollbackMessage: ChatMessage = {
        id: Date.now().toString(),
        type: "system",
        content: `Rolled back to checkpoint: ${checkpoint.description}`,
        timestamp: Date.now(),
      };
      setChatHistory((prev) => [...prev, rollbackMessage]);

      // Close the checkpoints panel
      setShowCheckpoints(false);
    }
  };

  const toggleCheckpoints = () => {
    setShowCheckpoints(!showCheckpoints);
    if (showRecentPatterns) setShowRecentPatterns(false);
  };

  const toggleRecentPatterns = () => {
    setShowRecentPatterns(!showRecentPatterns);
    if (showCheckpoints) setShowCheckpoints(false);
  };

  const toggleChatVisibility = () => {
    setIsChatVisible(!isChatVisible);
  };

  const copyPatternToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedPattern);
      setCopySuccess(true);
      console.log("Pattern copied to clipboard");

      // Show toast notification
      setToast({
        message: "Pattern copied to clipboard!",
        visible: true,
        type: "success",
      });

      // Reset success message after 2 seconds
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy pattern:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = generatedPattern;
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand("copy");
        setCopySuccess(true);
        setToast({
          message: "Pattern copied to clipboard!",
          visible: true,
          type: "success",
        });
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      } catch (err) {
        console.error("Fallback copy failed:", err);
        setToast({
          message: "Failed to copy pattern",
          visible: true,
          type: "error",
        });
      }

      document.body.removeChild(textArea);
    }
  };

  // Main editor layout - adjusted for the user avatar sidebar
  return (
    <div className="w-full h-[calc(100vh-64px-80px)] flex flex-col md:flex-row">
      {/* Left panel - Chat */}
      {isChatVisible && (
        <>
          <div
            className="w-full md:w-auto border-r border-[#30363d] flex flex-col bg-[#161b22]"
            style={{ width: `${chatWidth}px` }}
          >
            <ChatPanel
              chatHistory={chatHistory}
              onSendMessage={handlePromptSubmit}
              isGenerating={isGenerating}
              progressTasks={progressTasks}
              progress={progress}
              progressMessage={progressMessage}
              onToggleCheckpoints={toggleCheckpoints}
              onToggleRecentPatterns={toggleRecentPatterns}
              onToggleChatVisibility={toggleChatVisibility}
              showCheckpoints={showCheckpoints}
              showRecentPatterns={showRecentPatterns}
              checkpoints={checkpoints}
              onRollbackToCheckpoint={rollbackToCheckpoint}
              recentPatterns={recentPatterns}
            />
          </div>

          {/* Resize handle */}
          <div
            ref={resizeRef}
            className="w-1 cursor-ew-resize bg-transparent hover:bg-[#1f6feb]/50 transition-colors flex items-center justify-center"
            onMouseDown={startResize}
          >
            <GripVertical className="h-6 w-6 text-gray-600 hover:text-[#58a6ff]" />
          </div>
        </>
      )}

      {/* Right panel - Preview/Code */}
      <PreviewPanel
        pattern={generatedPattern}
        viewMode={viewMode}
        onChangeViewMode={setViewMode}
        onChangePattern={setGeneratedPattern}
        onCopyPattern={copyPatternToClipboard}
        copySuccess={copySuccess}
        isGenerating={isGenerating}
        progressTasks={progressTasks}
        progress={progress}
        progressMessage={progressMessage}
        onToggleChatVisibility={toggleChatVisibility}
        isChatVisible={isChatVisible}
      />

      {/* Toast notification - rendered at the document level to avoid layout shifts */}
      {createPortal(<Toast />, document.body)}
    </div>
  );

  // Toast notification component
  function Toast() {
    if (!toast.visible) return null;

    return (
      <div className="fixed bottom-4 right-4 z-[9999] animate-fade-in-up pointer-events-none">
        <div
          className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
            toast.type === "success"
              ? "bg-[#238636] text-white"
              : "bg-red-500 text-white"
          }`}
        >
          {toast.type === "success" ? (
            <Check className="h-5 w-5" />
          ) : (
            <X className="h-5 w-5" />
          )}
          <span className="font-medium">{toast.message}</span>
        </div>
      </div>
    );
  }
}
